import { object } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import {
  isLineItemScoped,
  isOptionScoped,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanTaskOptimized,
  ScanTemplateOption,
} from '@kaiizen/kai-scan-template-system';
import React, { useCallback, useMemo } from 'react';

import { ScanDataMutators, useScanPartNotes } from '../../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { CheckIconTight } from '../../Widgets/CheckIcon';
import { InlineLineItemEditor } from './InlineLineItemEditor';
import { ScanPartNotesButton } from './ScanPartNotesButton';

const OPTION_CONTENT_CELL_STYLE = css`
  label: OPTION_CONTENT_CELL_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--background);
  border: 1px solid var(--border2);
`;

const OPTION_HEADER_STYLE = css`
  label: OPTION_HEADER_STYLE;
  display: flex;
  flex-align: center;
  cursor: pointer;
  min-height: ${LAYOUT2.spacing5}px;

  gap: ${LAYOUT2.spacing1}px;
`;

const OPTION_HEADER_TITLE_BAR_STYLE = css`
  label: OPTION_HEADER_TITLE_BAR_STYLE;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  min-height: ${LAYOUT2.spacing5}px;

  padding-left: ${LAYOUT2.spacing1}px;
  gap: ${LAYOUT2.spacing1}px;
`;

const OPTION_TITLE_ROW_STYLE = css`
  label: OPTION_TITLE_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  justify-content: flex-end;
`;

const OPTION_TITLE_COLUMN_STYLE = css`
  label: OPTION_TITLE_COLUMN_STYLE;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const OPTION_TITLE_INNER_ROW_STYLE = css`
  label: OPTION_TITLE_INNER_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const OPTION_DESCRIPTION_CONTAINER_STYLE = css`
  label: OPTION_DESCRIPTION_CONTAINER_STYLE;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: ${LAYOUT2.spacingP5}px;
`;

const OPTION_TITLE_STYLE = css`
  label: OPTION_TITLE_STYLE;
  ${TYPOGRAPHY2.tiny}
  color: var(--text);
  text-transform: uppercase;
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  padding: ${LAYOUT2.spacingP5}px 0;
`;

const OPTION_NOTES_COLUMN_STYLE = css`
  label: OPTION_NOTES_COLUMN_STYLE;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 45px;
  height: 45px;
`;

const OPTION_DESCRIPTION_STYLE = css`
  label: OPTION_DESCRIPTION_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const LINE_ITEMS_CONTAINER_STYLE = css`
  label: LINE_ITEMS_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: auto;
`;

export const InlineOptionEditor = React.memo(({
  optionTemplate,
  option,
  task,
  groupId,
  scanDataMutators,
  expanded,
}: {
  optionTemplate: ScanTemplateOption;
  option?: ScanOptionOptimized;
  task: ScanTaskOptimized;
  groupId: string;
  scanDataMutators: ScanDataMutators;
  expanded?: boolean;
}) => {
  const scopedLineItemIds = useMemo(() => object(option?.lineItems, { with: isLineItemScoped, withKey: lineItem => lineItem.id }), [option?.lineItems]);
  const optionIsScoped = option && isOptionScoped(option);
  const { scanPartNotes: notes } = useScanPartNotes(
    option?.id,
  );

  const handleCreateOption = useCallback(() => {
    const firstLineItemTemplate = optionTemplate.lineItemTemplates?.[0];
    if (!firstLineItemTemplate) return;
    const firstValueKey = Object.keys(firstLineItemTemplate.values ?? {})[0];
    if (!firstValueKey) return;

    scanDataMutators.addOption({
      groupId,
      taskId: task.id,
      optionTemplateId: optionTemplate.id,
      addLineItemTemplateId: firstLineItemTemplate.id,
      addLineItemInitializers: {
        values: { [firstValueKey]: { setValue: 0 } },
      },
    });
  }, [groupId, task.id, optionTemplate, scanDataMutators]);

  return (
    <div css={OPTION_CONTENT_CELL_STYLE}>
      <div css={OPTION_HEADER_STYLE}>
        <div css={OPTION_HEADER_TITLE_BAR_STYLE}>
          <div css={OPTION_TITLE_ROW_STYLE}>
            <div css={OPTION_TITLE_COLUMN_STYLE}>
              <div css={OPTION_TITLE_INNER_ROW_STYLE}>
                {optionIsScoped && <CheckIconTight color='var(--textGreen)' />}
                <div css={OPTION_TITLE_STYLE}>
                  {optionTemplate.name}
                </div>
              </div>
              {optionTemplate.description && (
                <div css={OPTION_DESCRIPTION_CONTAINER_STYLE}>
                  <div css={OPTION_DESCRIPTION_STYLE}>{optionTemplate.description}</div>
                </div>
              )}
            </div>
            <div css={OPTION_NOTES_COLUMN_STYLE}>
              <ScanPartNotesButton
                scanPart={option}
                onCreateScanPart={handleCreateOption}
                notes={notes}
              />
            </div>
          </div>
        </div>
      </div>
      {(expanded || optionIsScoped) && (
        <div css={LINE_ITEMS_CONTAINER_STYLE}>
          {optionTemplate?.lineItemTemplates?.map(lineItemTemplate => {
            const filtered = option?.lineItems?.filter(l => l.templateId === lineItemTemplate.id);
            const lineItems: (ScanLineItemOptimized | undefined)[] = filtered?.length ? filtered : [undefined];
            return lineItems.map(lineItem => {
              const scoped = lineItem && scopedLineItemIds[lineItem.id];
              if (!scoped && !expanded) return null;

              return (
                <InlineLineItemEditor
                  key={lineItem?.id ?? lineItemTemplate.id}
                  lineItemTemplate={lineItemTemplate}
                  optionTemplate={optionTemplate}
                  lineItem={lineItem}
                  taskId={task.id}
                  groupId={groupId}
                  optionId={option?.id}
                  scanDataMutators={scanDataMutators}
                />
              );
            });
          })}
        </div>
      )}
    </div>
  );
});
